import { RIDE_STATUSES, RIDE_TERM_REASONS } from "score-common";
import { canMarkRideRuling } from "./rideRuling";

// Card #2798 -- the "End Ride" flow (in the Utility menu since #3609). One scribe-facing action
// ("end this ride as X") over four outcomes whose endpoints are NOT uniform:
//
//   Scratch    PUT  /ride/:id/status  body { status: SCRATCHED, termReason }
//              (#916; any assigned seat: DR126.3.3 makes it secretary-side)
//   No Show    C seat:   POST /ride/:id/no-show      (#1992, no body, C or admin)
//              other:    PUT  /ride/:id/status       (#916 term reason, any seat)
//   Withdrawn  POST /ride/:id/withdrawn  (#2795, no body, C or admin: DR124.5)
//   Excused    POST /ride/:id/excused    (#2064, no body, C or admin: DR123.3)
//
// That inconsistency lives here and nowhere else: callers pick an outcome and
// get a promise, never a status code or a request shape.

export const END_RIDE_OUTCOMES = Object.freeze({
  SCRATCH: "scratch",
  NO_SHOW: "noShow",
  WITHDRAWN: "withdrawn",
  EXCUSED: "excused",
});

// Long enough for a slow venue connection, short enough that a hang ends.
export const END_RIDE_TIMEOUT_MS = 15000;

// Menu order, exactly as the card specifies.
const MENU_ORDER = [
  END_RIDE_OUTCOMES.SCRATCH,
  END_RIDE_OUTCOMES.NO_SHOW,
  END_RIDE_OUTCOMES.WITHDRAWN,
  END_RIDE_OUTCOMES.EXCUSED,
];

// Withdrawn and Excused are rulings of the judge at C (DR124.5, DR123.3) and
// score-api refuses any other seat, so a seat that cannot make them is never
// offered them. Scratch and No Show stay available at every seat.
const C_ONLY = new Set([END_RIDE_OUTCOMES.WITHDRAWN, END_RIDE_OUTCOMES.EXCUSED]);

export function offeredEndRideOutcomes(judgePosition) {
  return MENU_ORDER.filter(
    (outcome) => !C_ONLY.has(outcome) || canMarkRideRuling(judgePosition)
  );
}

// terminationReason.source values End Ride's outcomes stamp (score-api's
// markRideNoShow / markRideExcused / markRideWithdrawn / setRideStatus).
const ENDED_SOURCES = ["no-show", "excused", "withdrawn", "scratch"];

/**
 * Has the ride been ended by one of End Ride's four outcomes? Reads the ride
 * row only: No Show / Excused / Withdrawn write ride.noShow / ride.excused /
 * ride.withdrawn + ride.terminationReason and never status SCRATCHED, so
 * status alone misses three of the four. A sibling judge's elimination is a
 * different path (isRideEliminationTerminated) and is not included.
 */
export function isRideEnded(ride) {
  return (
    ride?.status === RIDE_STATUSES.SCRATCHED ||
    ride?.noShow?.active === true ||
    ride?.excused?.active === true ||
    ride?.withdrawn?.active === true ||
    ENDED_SOURCES.includes(ride?.terminationReason?.source)
  );
}

/**
 * Record `outcome` on the ride. Resolves once the write has landed; rejects
 * with the generated client's error otherwise (render it with
 * endRideErrorMessage, never raw).
 *
 * @param {object} rides  scoreApi.rides (the generated RidesApi)
 */
export async function endRide(rides, rideId, outcome, judgePosition, { timeoutMs = END_RIDE_TIMEOUT_MS } = {}) {
  // Every call carries a request timeout: the confirm dialog's Cancel is
  // disabled while the write is in flight, so a hung request would otherwise
  // strand the scribe on a dialog they cannot leave. A timeout rejects like
  // any other failure (no response), which renders as the plain fallback.
  const opts = { timeout: timeoutMs };
  switch (outcome) {
    case END_RIDE_OUTCOMES.SCRATCH:
      return rides.setRideStatus(
        rideId,
        { status: RIDE_STATUSES.SCRATCHED, termReason: RIDE_TERM_REASONS.SCRATCH },
        opts
      );
    case END_RIDE_OUTCOMES.NO_SHOW:
      return canMarkRideRuling(judgePosition)
        ? rides.markRideNoShow(rideId, opts)
        : rides.setRideStatus(
            rideId,
            { status: RIDE_STATUSES.SCRATCHED, termReason: RIDE_TERM_REASONS.NO_SHOW },
            opts
          );
    case END_RIDE_OUTCOMES.WITHDRAWN:
      return rides.markRideWithdrawn(rideId, opts);
    case END_RIDE_OUTCOMES.EXCUSED:
      return rides.markRideExcused(rideId, opts);
    default:
      throw new Error(`Unknown End Ride outcome: ${outcome}`);
  }
}

/**
 * The text to show for a failed End Ride write. score-api's own sentence when
 * the response carries one (a conflicting outcome on an already-terminated
 * ride says what it already is and who clears it); otherwise the supplied
 * fallback. Never err.message: the generated client's own is "Request failed
 * with status code 409" (#2794: no status codes on screen).
 */
export function endRideErrorMessage(err, fallback) {
  const body = err?.response?.data;
  const message = body?.data?.message ?? body?.message;
  return typeof message === "string" && message.trim() ? message : fallback;
}

/**
 * Did score-api refuse a No Show because the ride already has a mark (reason
 * code ride_has_marks, #3609)? A sibling judge's marks are on another tablet,
 * so this tablet can learn it only from the server's answer. The scribe sees
 * the server's own sentence (endRideErrorMessage) and the menu greys No Show
 * out; nothing here retries the write.
 */
export function isRideHasMarksError(err) {
  return err?.response?.data?.code === "ride_has_marks";
}
